export const canUseDOM = !!(
  typeof window !== `undefined` &&
  window.document &&
  window.document.createElement
)

export const capitalize = (string: string) =>
  string[0].toUpperCase() + string.slice(1)

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => {}

export const pluralise = (count: number, plural: string, singular: string) =>
  count === 1 ? singular : plural

export function omit<T extends object, K extends Extract<keyof T, string>>(
  obj: T,
  ...keys: K[]
): Omit<T, K> {
  const copy = { ...obj }
  keys.forEach(key => {
    delete copy[key]
  })

  return copy
}

export function pick<T extends object, K extends keyof T>(
  base: T,
  ...keys: K[]
): Pick<T, K> {
  const entries = keys.map(key => [key, base[key]])
  return Object.fromEntries(entries)
}

export function debounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  timeout = 300
) {
  let timer: ReturnType<typeof setTimeout>
  const debounced = (...args: Parameters<F>) => {
    clearTimeout(timer)
    timer = setTimeout(() => func(...args), timeout)
  }
  debounced.cancel = () => clearTimeout(timer)
  return debounced
}

export const toDecimalPlaces = (value: number, places = 2) =>
  Math.round(value * 10 ** places) / 10 ** places

export const getImageCdnUrl = (imageUrl: string, width = 120, height = 120) =>
  `https://res.cloudinary.com/wolfandbadger/image/upload/s--YqyalqLY--/q_auto:good,f_auto,e_sharpen,w_${width},h_${height},c_pad/${imageUrl}`

export const isLocalStorageAvailable = () => {
  try {
    window.localStorage.setItem(`wb_local_storage_test`, `value`)
    window.localStorage.removeItem(`wb_local_storage_test`)
    return true
  } catch {
    return false
  }
}

export const downloadFileFromUrl = (url: string) => {
  let downloadUrl = url
  // Rewrite Cloudinary urls since Safari will open certain file types rather
  // than download unless content-disposition headers are set
  if (downloadUrl.includes(`/raw/upload/exports/`)) {
    downloadUrl = downloadUrl.replace(`/exports/`, `/fl_attachment/exports/`)
  }

  const downloadButton = document.createElement(`a`)
  downloadButton.href = downloadUrl
  downloadButton.setAttribute(`download`, ``)
  downloadButton.click()
}

const rtf = new Intl.RelativeTimeFormat(`en`, { numeric: `auto` })

export const getRelativeTimeString = (date: Date | number) => {
  const timeMs = typeof date === `number` ? date : date.getTime()
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000)
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * 30,
    86400 * 365,
    Infinity
  ]
  const units: Intl.RelativeTimeFormatUnit[] = [
    `second`,
    `minute`,
    `hour`,
    `day`,
    `week`,
    `month`,
    `year`
  ]
  const unitIndex = cutoffs.findIndex(cutoff => cutoff > Math.abs(deltaSeconds))
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex])
}
